'use client';
// Border colour is a raw hex without a token, same as ProductDetailsSectionWrapper.
/* eslint-disable tailwindcss/no-arbitrary-value */

import classNames from 'classnames';
import { usePathname } from 'next/navigation';
import { PropsWithChildren, useState } from 'react';
import { CmsProduct } from '../../../types/cmsProduct';
import { PushDataLayerFn } from '../../../types/pushDataLayer';
import {
  ComponentTypeEnum,
  sendProductAnalytics,
  UserActionEnum,
} from '../../../utils/analytics';
import { MaterialIcon } from '../../MaterialIcon';

/**
 * Grey collapsible info block (e.g. an offer's "Terms and Conditions") — a dumb accordion card with
 * optional expand / collapse tracking. Used by the Offers sections; data source agnostic.
 */
export type GrayInfoBlockProps = PropsWithChildren & {
  title: string;
  /** Title typography; defaults to the table-cell-primary style of the CC/TI Offers section. */
  titleClassName?: string;
  /** Heading level of the title: h3 under a section's h2, h4 when the block sits under an h3. */
  headingLevel?: 'h3' | 'h4';
  /**
   * Accordion open/collapse tracking (CMS-1784 #2405). The visible label is
   * `title` (e.g. "Terms and Conditions"); `trackingTitle` (the offer title)
   * identifies which offer in the analytics event.
   */
  product?: CmsProduct;
  pushDataLayer?: PushDataLayerFn;
  sectionTitle?: string;
  trackingTitle?: string;
};

export const GrayInfoBlock: React.FC<GrayInfoBlockProps> = ({
  children,
  title,
  titleClassName = 'mhg-alpha-typography-table-cell-primary',
  headingLevel: Heading = 'h3',
  product,
  pushDataLayer,
  sectionTitle,
  trackingTitle,
}) => {
  const [sectionVisible, setSectionVisible] = useState(false);
  // Page path as the product-list name, aligned with the rest of the PDP.
  const itemListName = usePathname() ?? undefined;

  const handleToggle = () => {
    const nextVisible = !sectionVisible;
    setSectionVisible(nextVisible);

    if (product && pushDataLayer && trackingTitle) {
      sendProductAnalytics({
        event: 'product_interaction',
        action: nextVisible ? UserActionEnum.EXPAND : UserActionEnum.COLLAPSE,
        componentType: ComponentTypeEnum.ACCORDION,
        componentTitle: `Product Details Module/${
          sectionTitle ? `${sectionTitle}_` : ''
        }${trackingTitle}`,
        itemListName,
        product,
        index: '0',
        pushDataLayer,
      });
    }
  };

  return (
    <div
      className="rounded-lg bg-fill/alternative-1 border-[#01000A14] p-4 flex flex-col gap-3"
      data-component="pdp-gray-info-block"
    >
      {/* T&C sub-section title as a heading wrapping its toggle, matching
          ProductDetailsSectionWrapper (WAI-ARIA accordion pattern). */}
      <Heading className="m-0">
        <button
          type="button"
          className="flex w-full cursor-pointer items-center justify-between"
          onClick={handleToggle}
          aria-expanded={sectionVisible}
        >
          <span className={titleClassName}>{title}</span>

          <MaterialIcon
            icon="expand_less"
            className={`transition-transform ${sectionVisible ? 'rotate-0' : 'rotate-180'}`}
          />
        </button>
      </Heading>
      <div className={classNames({ hidden: !sectionVisible })}>{children}</div>
    </div>
  );
};
